import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

import { db } from "../../FirebaseConfig";
import { useCollection } from "../../lib/firestore-hooks";
import {
  dowEs,
  formatFecha,
  formatFechaShort,
  formatHora,
  todaySantiago,
} from "../../lib/format";
import type {
  AeropuertoDoc,
  AvionDoc,
  DistanciaDoc,
  VueloDoc,
} from "../../lib/types";
import { distanciaId } from "../../lib/types";

interface VueloRow extends VueloDoc {
  id: string;
}

/**
 * Where an aircraft lands after a given flight, derived from
 * `postFlightDisposition`. Used to compute pre-ferries on subsequent
 * flights. Null disposition is treated as "stay" — same as the v1
 * convention.
 */
function flightPostLocation(
  flight: VueloRow,
  homeBaseIcao: string | null
): string | null {
  switch (flight.postFlightDisposition) {
    case "home":
      return homeBaseIcao;
    case "relocate_other":
      return flight.postFlightDestinationIcao;
    case "stay":
    default:
      return flight.destinationIcao;
  }
}

/**
 * Where the aircraft is sitting just before `flight` takes off. Walks
 * earlier approved/completed flights for the same aircraft and uses the
 * most recent one's post-location; falls back to home base when none.
 *
 * `flights` must be sorted by startAt ascending.
 */
function aircraftLocationBefore(
  flight: VueloRow,
  flights: VueloRow[],
  homeBaseIcao: string | null
): string | null {
  let prev: VueloRow | null = null;
  for (const f of flights) {
    if (f.id === flight.id) break;
    if (f.aircraftId !== flight.aircraftId) continue;
    if (f.status !== "approved" && f.status !== "completed") continue;
    prev = f;
  }
  if (!prev) return homeBaseIcao;
  return flightPostLocation(prev, homeBaseIcao);
}

interface FerryLeg {
  direction: "pre" | "post";
  fromIcao: string | null;
  toIcao: string | null;
  /** Approx departure HH:MM in the flight's local (Santiago) wall clock. */
  approxHora: string | null;
  distanceNm: number | null;
  flightTimeMin: number | null;
}

const FERRY_GROUND_BUFFER_MIN = 30;
const MIN_FERRY_MIN = 30;

function computeFerryMin(
  distanceNm: number | null,
  speedKt: number | null
): number {
  if (!distanceNm || !speedKt || speedKt <= 0) return MIN_FERRY_MIN;
  const raw = Math.ceil((distanceNm / speedKt) * 60);
  return Math.max(MIN_FERRY_MIN, Math.ceil(raw / 15) * 15);
}

/** "13:00" + 90 -> "14:30"; null if hora is malformed. */
function addMinutesHora(hora: string, mins: number): string | null {
  const m = hora.match(/^(\d{2}):(\d{2})/);
  if (!m) return null;
  const h0 = parseInt(m[1]!, 10);
  const m0 = parseInt(m[2]!, 10);
  if (Number.isNaN(h0) || Number.isNaN(m0)) return null;
  const total = h0 * 60 + m0 + mins;
  const norm = ((total % 1440) + 1440) % 1440;
  const hh = Math.floor(norm / 60).toString().padStart(2, "0");
  const mm = (norm % 60).toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

interface FlightBlock {
  flight: VueloRow;
  preFerry: FerryLeg | null;
  postFerry: FerryLeg | null;
}

/**
 * Apply a card-style frame to a flight row, with two flavors:
 *   - "alone" — flight has no ferries: subtle slate-200 frame with
 *     rounded corners so the row reads as its own card.
 *   - "top" / "middle" / "bottom" — flight is part of a flight + ferry
 *     block: 2px amber-400 outer edges only; nothing between the rows
 *     of the same block so they read as a continuous card.
 *
 * Used with `border-separate border-spacing-0` on the table so the
 * cell borders don't merge and the rounded corners stay sharp.
 */
function frameBorders(
  pos: "alone" | "top" | "middle" | "bottom",
  idx: number,
  lastIdx: number
): string {
  if (pos === "alone") {
    const lCls = idx === 0 ? "border-l border-l-slate-200" : "";
    const rCls = idx === lastIdx ? "border-r border-r-slate-200" : "";
    let rounded = "";
    if (idx === 0) rounded = "rounded-l-md";
    else if (idx === lastIdx) rounded = "rounded-r-md";
    return `border-t border-b border-t-slate-200 border-b-slate-200 ${lCls} ${rCls} ${rounded}`;
  }
  const tCls = pos === "top" ? "border-t-2 border-t-amber-400" : "";
  const bCls = pos === "bottom" ? "border-b-2 border-b-amber-400" : "";
  const lCls = idx === 0 ? "border-l-2 border-l-amber-400" : "";
  const rCls = idx === lastIdx ? "border-r-2 border-r-amber-400" : "";
  let rounded = "";
  if (pos === "top" && idx === 0) rounded = "rounded-tl-lg";
  else if (pos === "top" && idx === lastIdx) rounded = "rounded-tr-lg";
  else if (pos === "bottom" && idx === 0) rounded = "rounded-bl-lg";
  else if (pos === "bottom" && idx === lastIdx) rounded = "rounded-br-lg";
  return `${tCls} ${bCls} ${lCls} ${rCls} ${rounded}`;
}

/**
 * /admin/flights — full Vuelos list with toggles for past + cancelled.
 * Same table layout as Resumen but unfiltered by default (status / date).
 * Click a row to drill into the flight detail.
 */
export function AdminVuelos() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<VueloRow[] | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showFerries, setShowFerries] = useState(true);
  // Aircraft filter: null = show every aircraft (default), otherwise the
  // aircraftId to restrict the list to (e.g. only Cirrus or only Pilatus).
  const [aircraftFilter, setAircraftFilter] = useState<string | null>(null);

  const { rows: aviones } = useCollection<AvionDoc>("Aviones");
  const { rows: aeropuertos } = useCollection<AeropuertoDoc>("Aeropuertos");
  const { rows: distancias } = useCollection<DistanciaDoc>("Distancias");

  const homeBaseByAircraft = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of aviones ?? []) map.set(a.id, a.homeBaseIcao);
    return map;
  }, [aviones]);
  const speedByAircraft = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of aviones ?? []) map.set(a.id, a.speedKt);
    return map;
  }, [aviones]);
  const distanceByPair = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of distancias ?? []) map.set(d.id, d.distanceNm);
    return map;
  }, [distancias]);
  const aeropuertoLabelByIcao = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of aeropuertos ?? []) {
      const shown = a.id.replace(/^_/, "");
      map.set(a.id, `${a.displayName} (${shown})`);
    }
    return map;
  }, [aeropuertos]);

  useEffect(() => {
    // Order by fecha server-side (single field — auto-indexed); sort by
    // hora client-side so we don't depend on the composite index being
    // already built before the screen can render.
    const q = query(collection(db, "Vuelos"), orderBy("fecha"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: VueloRow[] = [];
        snap.forEach((d) =>
          list.push({ id: d.id, ...(d.data() as VueloDoc) })
        );
        list.sort((a, b) => {
          if (a.fecha !== b.fecha) return a.fecha < b.fecha ? -1 : 1;
          return a.hora < b.hora ? -1 : a.hora > b.hora ? 1 : 0;
        });
        setRows(list);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error("[AdminVuelos] snapshot error:", err);
      }
    );
    return () => unsub();
  }, []);

  if (rows === null) {
    return <p className="text-sm text-slate-500">Cargando vuelos…</p>;
  }

  const today = todaySantiago();
  const totalPast = rows.filter((f) => f.fecha < today).length;
  const totalCancelled = rows.filter(
    (f) => f.status === "cancelled" || f.status === "rejected",
  ).length;
  // Status/date filter first (past + cancelled toggles). The aircraft
  // pill counts are computed off this set so each pill shows how many
  // flights it would reveal given the current toggles — independent of
  // which aircraft is currently selected.
  const visibleStatus = rows.filter(
    (f) =>
      (showCancelled || (f.status !== "cancelled" && f.status !== "rejected")) &&
      (showPast || f.fecha >= today)
  );
  const countByAircraft = new Map<string, number>();
  for (const f of visibleStatus) {
    countByAircraft.set(f.aircraftId, (countByAircraft.get(f.aircraftId) ?? 0) + 1);
  }
  // Aircraft pill options: active aircraft plus any that still have
  // flights in the current view, sorted by display name for stable order.
  const aircraftOptions = (aviones ?? [])
    .filter((a) => a.active || countByAircraft.has(a.id))
    .slice()
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const visible = visibleStatus.filter(
    (f) => aircraftFilter === null || f.aircraftId === aircraftFilter
  );

  // Build per-flight blocks: each block bundles its flight with optional
  // pre/post ferry legs so they share a single amber frame in the table.
  // Return-flight linkage lookups (parent + child). Used in both the
  // desktop table and the mobile card to mark a flight as "↩ Retorno
  // de X" or "↪ Con vuelo de retorno Y" so the relationship is
  // visible on the list, not just in the per-flight detail.
  const returnByOriginalId = new Map<string, VueloRow>();
  const flightById = new Map<string, VueloRow>();
  for (const v of rows) {
    flightById.set(v.id, v);
    if (v.returnOfFlightId) returnByOriginalId.set(v.returnOfFlightId, v);
  }

  // Compute ferries unconditionally for every approved flight so the
  // "Ferries (N)" pill count reflects the true total — independent of
  // whether the toggle is on. We strip them from the render blocks
  // below when the toggle is off.
  const allBlocks: FlightBlock[] = visible.map((f) => {
    const isApprovedish = f.status === "approved" || f.status === "completed";
    if (!isApprovedish) {
      return { flight: f, preFerry: null, postFerry: null };
    }
    const homeBase = homeBaseByAircraft.get(f.aircraftId) ?? null;
    const speed = speedByAircraft.get(f.aircraftId) ?? null;

    let preFerry: FerryLeg | null = null;
    const prevLoc = aircraftLocationBefore(f, rows, homeBase);
    if (prevLoc && prevLoc !== f.originIcao) {
      // Pre-ferry arrives FERRY_GROUND_BUFFER_MIN before takeoff; depart
      // that long plus the ferry's flight time earlier.
      const dist = distanceByPair.get(distanciaId(prevLoc, f.originIcao)) ?? null;
      const ferryMin = computeFerryMin(dist, speed);
      const offsetMin = -(FERRY_GROUND_BUFFER_MIN + ferryMin);
      preFerry = {
        direction: "pre",
        fromIcao: prevLoc,
        toIcao: f.originIcao,
        approxHora: addMinutesHora(f.hora, offsetMin),
        distanceNm: dist,
        flightTimeMin: ferryMin,
      };
    }

    let postFerry: FerryLeg | null = null;
    let toIcao: string | null = null;
    if (f.postFlightDisposition === "home") toIcao = homeBase;
    else if (f.postFlightDisposition === "relocate_other") {
      toIcao = f.postFlightDestinationIcao;
    }
    if (toIcao && toIcao !== f.destinationIcao) {
      // Post-ferry leaves FERRY_GROUND_BUFFER_MIN after the flight
      // arrives. The flight's arrival time = hora + flightTimeMin.
      const arrivalOffset = (f.flightTimeMin ?? 60) + FERRY_GROUND_BUFFER_MIN;
      const dist = distanceByPair.get(distanciaId(f.destinationIcao, toIcao)) ?? null;
      const ferryMin = computeFerryMin(dist, speed);
      postFerry = {
        direction: "post",
        fromIcao: f.destinationIcao,
        toIcao,
        approxHora: addMinutesHora(f.hora, arrivalOffset),
        distanceNm: dist,
        flightTimeMin: ferryMin,
      };
    }
    return { flight: f, preFerry, postFerry };
  });
  const totalFerryRows = allBlocks.reduce(
    (n, b) => n + (b.preFerry ? 1 : 0) + (b.postFerry ? 1 : 0),
    0
  );
  // Render blocks: drop ferries when the toggle is off so existing
  // `block.preFerry ? ...` checks at every render site keep working
  // without needing an additional `&& showFerries` everywhere.
  const blocks: FlightBlock[] = showFerries
    ? allBlocks
    : allBlocks.map((b) => ({ ...b, preFerry: null, postFerry: null }));

  return (
    <div className="space-y-5">
      <header>
        <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Vuelos</h1>
          <Link
            to="/admin/calendar"
            className="text-sm text-sky-700 hover:text-sky-900"
          >
            Ver calendario →
          </Link>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {rows.length} vuelo{rows.length === 1 ? "" : "s"} cargado
          {rows.length === 1 ? "" : "s"}.
        </p>
        {aircraftOptions.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <FilterPill
              label="Todos"
              count={visibleStatus.length}
              active={aircraftFilter === null}
              onClick={() => setAircraftFilter(null)}
            />
            {aircraftOptions.map((a) => (
              <FilterPill
                key={a.id}
                label={a.displayName}
                count={countByAircraft.get(a.id) ?? 0}
                active={aircraftFilter === a.id}
                onClick={() => setAircraftFilter(a.id)}
              />
            ))}
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
          <ToggleSwitch
            label="Mostrar pasados"
            count={totalPast}
            on={showPast}
            onChange={setShowPast}
          />
          <ToggleSwitch
            label="Mostrar cancelados"
            count={totalCancelled}
            on={showCancelled}
            onChange={setShowCancelled}
          />
          <ToggleSwitch
            label="Mostrar ferries"
            count={totalFerryRows}
            on={showFerries}
            onChange={setShowFerries}
          />
        </div>
      </header>

      {/* Mobile: stack of cards. The table renders on md+ only. */}
      <div className="md:hidden">
        <FlightCardsMobile
          blocks={blocks}
          today={today}
          aeropuertoLabelByIcao={aeropuertoLabelByIcao}
          returnByOriginalId={returnByOriginalId}
          flightById={flightById}
        />
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full border-separate border-spacing-0 text-xs">
          <thead className="bg-slate-100 text-left text-[10px] uppercase tracking-wide text-slate-600">
            <tr>
              <th className="border-b border-slate-200 px-2 py-2">Fecha</th>
              <th className="border-b border-slate-200 px-2 py-2">Hora</th>
              <th className="border-b border-slate-200 px-2 py-2">Avión</th>
              <th className="border-b border-slate-200 px-2 py-2">Ruta</th>
              <th className="border-b border-slate-200 px-2 py-2">Cta</th>
              <th className="border-b border-slate-200 px-2 py-2">Solicita</th>
              <th className="border-b border-slate-200 px-2 py-2">Piloto</th>
              <th className="border-b border-slate-200 px-2 py-2">Status</th>
              <th className="border-b border-slate-200 px-2 py-2 text-center">PAX</th>
              <th className="min-w-[14rem] border-b border-slate-200 px-2 py-2">Nota</th>
            </tr>
          </thead>
          <tbody>
            {blocks.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="px-3 py-8 text-center text-sm text-slate-500"
                >
                  Sin vuelos en este filtro.
                </td>
              </tr>
            ) : (
              blocks.flatMap((block, blockIdx) => {
                const lastCol = 9;
                const hasPre = block.preFerry != null;
                const hasPost = block.postFerry != null;
                // Position of the flight row inside its block — drives
                // the amber frame edges.
                const flightPos: "alone" | "top" | "middle" | "bottom" =
                  !hasPre && !hasPost
                    ? "alone"
                    : hasPre && hasPost
                      ? "middle"
                      : hasPre
                        ? "bottom"
                        : "top";

                const renderFerryRow = (
                  leg: FerryLeg,
                  pos: "top" | "bottom",
                  keyStr: string
                ) => {
                  const f = block.flight;
                  const fromLabel =
                    aeropuertoLabelByIcao.get(leg.fromIcao ?? "") ??
                    leg.fromIcao ??
                    "?";
                  const toLabel =
                    aeropuertoLabelByIcao.get(leg.toIcao ?? "") ??
                    leg.toIcao ??
                    "?";
                  const note =
                    leg.direction === "post"
                      ? `ferry para vuelo a ${f.destinationIcao.replace(/^_/, "")}`
                      : `ferry para vuelo desde ${f.originIcao.replace(/^_/, "")}`;
                  // De-saturate the ferry row when its parent flight
                  // is already in the past (or cancelled), so it visually
                  // tracks the muted parent row rather than glaring as
                  // a vivid amber stripe inside greyed-out history.
                  const ferryIsPast = f.fecha < today;
                  const ferryIsCancelled =
                    f.status === "cancelled" || f.status === "rejected";
                  const ferryRowCls = ferryIsCancelled
                    ? "ferry-cancelled hover:brightness-95 text-slate-500"
                    : ferryIsPast
                      ? "ferry-past hover:brightness-95 text-slate-500"
                      : "bg-teal-50 hover:bg-teal-100";
                  return (
                    <tr key={keyStr} className={ferryRowCls}>
                      <td
                        className={`whitespace-nowrap px-2 py-1.5 font-mono text-slate-700 ${frameBorders(pos, 0, lastCol)}`}
                      >
                        {formatFecha(f.fecha)}{" "}
                        <span className="text-slate-400">({dowEs(f.fecha)})</span>
                      </td>
                      <td
                        className={`whitespace-nowrap px-2 py-1.5 font-mono text-slate-500 ${frameBorders(pos, 1, lastCol)}`}
                      >
                        <div>{leg.approxHora ? `~${leg.approxHora}` : "~?"}</div>
                        <div className="font-sans text-[10px]">
                          {leg.distanceNm != null
                            ? `${Math.round(leg.distanceNm)} NM · `
                            : ""}
                          {leg.flightTimeMin ?? "?"} min
                        </div>
                      </td>
                      <td
                        className={`whitespace-nowrap px-2 py-1.5 text-slate-900 ${frameBorders(pos, 2, lastCol)}`}
                      >
                        {f.aircraftDisplayName}
                      </td>
                      <td
                        className={`w-[14rem] px-2 py-1.5 text-slate-900 ${frameBorders(pos, 3, lastCol)}`}
                      >
                        <div className="truncate">{fromLabel}</div>
                        <div className="truncate text-slate-500">→ {toLabel}</div>
                      </td>
                      <td
                        className={`whitespace-nowrap px-2 py-1.5 font-mono text-slate-700 ${frameBorders(pos, 4, lastCol)}`}
                      >
                        {f.accountCode ?? "—"}
                      </td>
                      <td
                        className={`whitespace-nowrap px-2 py-1.5 text-slate-400 italic ${frameBorders(pos, 5, lastCol)}`}
                      >
                        automático
                      </td>
                      <td
                        className={`whitespace-nowrap px-2 py-1.5 text-slate-700 ${frameBorders(pos, 6, lastCol)}`}
                      >
                        {f.pilotDisplayName ?? f.pilotDisplayNameRequested ?? "—"}
                      </td>
                      <td className={`whitespace-nowrap px-2 py-1.5 ${frameBorders(pos, 7, lastCol)}`}>
                        <StatusBadge status="ferry" />
                      </td>
                      <td
                        className={`whitespace-nowrap px-2 py-1.5 text-center text-slate-400 ${frameBorders(pos, 8, lastCol)}`}
                      >
                        —
                      </td>
                      <td
                        className={`min-w-[14rem] px-2 py-1.5 text-xs italic text-slate-500 ${frameBorders(pos, 9, lastCol)}`}
                      >
                        {note}
                      </td>
                    </tr>
                  );
                };

                const f = block.flight;
                const isPast = f.fecha < today;
                const isCancelled =
                  f.status === "cancelled" || f.status === "rejected";
                const isPending = f.status === "pending";
                let bg = blockIdx % 2 === 0 ? "bg-white" : "bg-slate-50";
                let hover = "hover:bg-sky-50";
                if (isCancelled) {
                  bg = "bg-rose-50 text-slate-600";
                  hover = "hover:bg-rose-100";
                } else if (isPast) {
                  bg = "flight-past text-slate-600";
                  hover = "hover:brightness-95";
                } else if (isPending) {
                  bg = "flight-pending";
                  hover = "hover:brightness-95";
                }

                const renderedRows = [];
                if (block.preFerry) {
                  renderedRows.push(
                    renderFerryRow(block.preFerry, "top", `${f.id}-pre`)
                  );
                }
                renderedRows.push(
                  <tr
                    key={f.id}
                    onClick={() => navigate(`/admin/flights/${f.id}`)}
                    className={`${bg} align-top cursor-pointer ${hover}`}
                    title={`Vuelo ${f.publicNumber}`}
                  >
                    <td className={`whitespace-nowrap px-2 py-1.5 font-mono leading-tight text-slate-700 ${frameBorders(flightPos, 0, lastCol)}`}>
                      <Link
                        to={`/admin/flights/${f.id}`}
                        className="hover:text-sky-700"
                      >
                        {formatFecha(f.fecha)}{" "}
                        <span className="text-slate-400">
                          ({dowEs(f.fecha)})
                        </span>
                      </Link>
                      {f.returnOfFlightId &&
                      flightById.get(f.returnOfFlightId) ? (
                        <div
                          className="text-[9px] leading-none text-slate-500"
                          title="Vuelo de retorno"
                        >
                          ↩ es retorno del{" "}
                          {formatFechaShort(
                            flightById.get(f.returnOfFlightId)!.fecha
                          )}
                        </div>
                      ) : null}
                      {returnByOriginalId.get(f.id) ? (
                        <div
                          className="text-[9px] leading-none text-slate-500"
                          title="Tiene un vuelo de retorno"
                        >
                          ↪ con retorno el{" "}
                          {formatFechaShort(
                            returnByOriginalId.get(f.id)!.fecha
                          )}
                        </div>
                      ) : null}
                    </td>
                    <td className={`whitespace-nowrap px-2 py-1.5 font-mono text-slate-700 ${frameBorders(flightPos, 1, lastCol)}`}>
                      <div>{formatHora(f.hora)}</div>
                      {f.distanceNm != null || f.flightTimeMin != null ? (
                        <div className="font-sans text-[10px] text-slate-500">
                          {f.distanceNm != null
                            ? `${Math.round(f.distanceNm)} NM · `
                            : ""}
                          {f.flightTimeMin ?? "?"} min
                        </div>
                      ) : null}
                    </td>
                    <td className={`whitespace-nowrap px-2 py-1.5 text-slate-900 ${frameBorders(flightPos, 2, lastCol)}`}>
                      {f.aircraftDisplayName}
                    </td>
                    <td className={`w-[14rem] px-2 py-1.5 text-slate-900 ${frameBorders(flightPos, 3, lastCol)}`}>
                      <div className="truncate">{f.originDisplay}</div>
                      <div className="truncate text-slate-500">
                        → {f.destinationDisplay}
                      </div>
                    </td>
                    <td className={`whitespace-nowrap px-2 py-1.5 font-mono text-slate-700 ${frameBorders(flightPos, 4, lastCol)}`}>
                      {f.accountCode ?? "—"}
                    </td>
                    <td className={`whitespace-nowrap px-2 py-1.5 text-slate-900 ${frameBorders(flightPos, 5, lastCol)}`}>
                      {f.requesterDisplayName}
                    </td>
                    <td className={`whitespace-nowrap px-2 py-1.5 text-slate-900 ${frameBorders(flightPos, 6, lastCol)}`}>
                      {f.pilotDisplayName ?? f.pilotDisplayNameRequested ?? "—"}
                    </td>
                    <td className={`whitespace-nowrap px-2 py-1.5 ${frameBorders(flightPos, 7, lastCol)}`}>
                      <span className="inline-flex items-center gap-1">
                        <StatusBadge status={f.status} />
                        {f.modificationRequest ? (
                          <span
                            className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-900"
                            title="Modificación pendiente de re-aprobación"
                          >
                            mod
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className={`whitespace-nowrap px-2 py-1.5 text-center font-mono text-slate-900 ${frameBorders(flightPos, 8, lastCol)}`}>
                      {f.numPax}
                    </td>
                    <td className={`min-w-[14rem] whitespace-pre-wrap break-words px-2 py-1.5 text-slate-600 ${frameBorders(flightPos, 9, lastCol)}`}>
                      {f.notes ?? ""}
                    </td>
                  </tr>
                );
                if (block.postFerry) {
                  renderedRows.push(
                    renderFerryRow(block.postFerry, "bottom", `${f.id}-post`)
                  );
                }
                // Spacer between blocks so adjacent flight cards don't
                // touch. The colspan covers the whole row so the gap
                // is uniform.
                if (blockIdx < blocks.length - 1) {
                  renderedRows.push(
                    <tr key={`${f.id}-spacer`} aria-hidden="true">
                      <td colSpan={10} className="h-1" />
                    </tr>
                  );
                }
                return renderedRows;
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    approved: "border border-emerald-200 bg-emerald-50 text-emerald-800",
    pending: "border border-amber-200 bg-amber-50 text-amber-800",
    rejected: "border border-rose-200 bg-rose-50 text-rose-800",
    cancelled: "border border-rose-200 bg-rose-50 text-rose-800",
    completed: "border border-slate-200 bg-slate-50 text-slate-600",
    ferry: "bg-sky-100 text-sky-800",
  };
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
        cls[status] ?? "bg-slate-100 text-slate-700"
      }`}
    >
      {status}
    </span>
  );
}

/**
 * Radio-style filter chip used for the aircraft filter row. Mutually
 * exclusive (only one active at a time), managed by the parent.
 */
function FilterPill({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition " +
        (active
          ? "border-brand-700 bg-brand-700 text-white"
          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50")
      }
    >
      {label}
      <span className={active ? "text-white/70" : "text-slate-400"}>
        ({count})
      </span>
    </button>
  );
}

function ToggleSwitch({
  label,
  count,
  on,
  onChange,
}: {
  label: string;
  count: number;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className="group inline-flex items-center gap-2"
    >
      <span
        className={
          "relative inline-flex h-5 w-9 items-center rounded-full transition " +
          (on ? "bg-sky-600" : "bg-slate-300 group-hover:bg-slate-400")
        }
      >
        <span
          className={
            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition " +
            (on ? "translate-x-4" : "translate-x-0.5")
          }
        />
      </span>
      <span className={"text-sm " + (on ? "text-slate-900" : "text-slate-500")}>
        {label}
        <span className="ml-1.5 text-xs text-slate-400">({count})</span>
      </span>
    </button>
  );
}

/**
 * Mobile (md-) card stack for the Vuelos list. Each block (pre +
 * flight + post) renders as a single rounded card; ferries get the
 * v1 amber border, single flights get a soft slate border. publicNumber
 * is intentionally pushed to the bottom in a small font — it's a
 * lookup id, not primary info.
 */
function FlightCardsMobile({
  blocks,
  today,
  aeropuertoLabelByIcao,
  returnByOriginalId,
  flightById,
}: {
  blocks: FlightBlock[];
  today: string;
  aeropuertoLabelByIcao: Map<string, string>;
  returnByOriginalId: Map<string, VueloRow>;
  flightById: Map<string, VueloRow>;
}) {
  if (blocks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
        Sin vuelos en este filtro.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {blocks.map((block) => {
        const f = block.flight;
        const isPast = f.fecha < today;
        const isCancelled =
          f.status === "cancelled" || f.status === "rejected";
        const isPending = f.status === "pending";
        const hasFerry = !!block.preFerry || !!block.postFerry;
        const wrap = hasFerry
          ? "rounded-lg border-2 border-teal-400 overflow-hidden"
          : "rounded-lg border border-slate-200 overflow-hidden";

        let flightBg = "bg-white";
        if (isCancelled) flightBg = "bg-rose-50";
        else if (isPast) flightBg = "flight-past";
        else if (isPending) flightBg = "flight-pending";

        return (
          <div key={f.id} className={wrap}>
            {block.preFerry ? (
              <FerryCardMobile
                flight={f}
                leg={block.preFerry}
                aeropuertoLabelByIcao={aeropuertoLabelByIcao}
                borderBelow
              />
            ) : null}
            <Link
              to={`/admin/flights/${f.id}`}
              className={`block ${flightBg} px-4 py-3 text-sm hover:bg-sky-50`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-mono text-xs text-slate-700">
                  <div>
                    {formatFecha(f.fecha)}{" "}
                    <span className="text-slate-400">({dowEs(f.fecha)})</span>{" "}
                    · {formatHora(f.hora)}
                  </div>
                  {f.distanceNm != null || f.flightTimeMin != null ? (
                    <div className="text-[10px] text-slate-500">
                      {f.distanceNm != null
                        ? `${Math.round(f.distanceNm)} NM · `
                        : ""}
                      {f.flightTimeMin ?? "?"} min
                    </div>
                  ) : null}
                </div>
                <StatusBadge status={f.status} />
              </div>
              <div className="mt-1 font-medium text-slate-900">
                {f.aircraftDisplayName}
              </div>
              <div className="mt-1 truncate text-sm text-slate-800">
                {f.originDisplay}{" "}
                <span className="whitespace-nowrap text-slate-500">→ {f.destinationDisplay}</span>
              </div>
              {f.returnOfFlightId &&
              flightById.get(f.returnOfFlightId) ? (
                <div className="mt-1 text-[11px] text-slate-500">
                  ↩ Es retorno del{" "}
                  {formatFechaShort(
                    flightById.get(f.returnOfFlightId)!.fecha
                  )}
                </div>
              ) : null}
              {returnByOriginalId.get(f.id) ? (
                <div className="mt-1 text-[11px] text-slate-500">
                  ↪ Con retorno el{" "}
                  {formatFechaShort(returnByOriginalId.get(f.id)!.fecha)}
                </div>
              ) : null}
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                <div>
                  <span className="text-slate-500">Solicita:</span>{" "}
                  {f.requesterDisplayName}
                </div>
                <div>
                  <span className="text-slate-500">Cta:</span>{" "}
                  <span className="font-mono">{f.accountCode ?? "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Piloto:</span>{" "}
                  {f.pilotDisplayName ?? f.pilotDisplayNameRequested ?? "—"}
                </div>
                <div>
                  <span className="text-slate-500">Pax:</span>{" "}
                  <span className="font-mono">{f.numPax}</span>
                </div>
              </div>
              {f.notes ? (
                <div className="mt-2 whitespace-pre-wrap text-xs text-slate-600">
                  {f.notes}
                </div>
              ) : null}
              <div className="mt-2 text-right text-[10px] font-mono text-slate-400">
                {f.publicNumber}
              </div>
            </Link>
            {block.postFerry ? (
              <FerryCardMobile
                flight={f}
                leg={block.postFerry}
                aeropuertoLabelByIcao={aeropuertoLabelByIcao}
                borderAbove
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function FerryCardMobile({
  flight,
  leg,
  aeropuertoLabelByIcao,
  borderAbove,
  borderBelow,
}: {
  flight: VueloRow;
  leg: FerryLeg;
  aeropuertoLabelByIcao: Map<string, string>;
  borderAbove?: boolean;
  borderBelow?: boolean;
}) {
  const fromLabel =
    aeropuertoLabelByIcao.get(leg.fromIcao ?? "") ?? leg.fromIcao ?? "?";
  const toLabel =
    aeropuertoLabelByIcao.get(leg.toIcao ?? "") ?? leg.toIcao ?? "?";
  const tipo = leg.direction === "post" ? "Ferry posterior" : "Ferry previo";
  const divider = borderAbove
    ? "border-t border-teal-300"
    : borderBelow
      ? "border-b border-teal-300"
      : "";
  // Match the parent flight's muted state (past/cancelled) so the
  // ferry doesn't pop as vivid amber inside a greyed-out card.
  const today = todaySantiago();
  const isPast = flight.fecha < today;
  const isCancelled =
    flight.status === "cancelled" || flight.status === "rejected";
  const bgCls = isCancelled
    ? "ferry-cancelled text-slate-500"
    : isPast
      ? "ferry-past text-slate-500"
      : "bg-teal-50 text-slate-700";
  return (
    <div className={`${bgCls} px-4 py-3 text-sm ${divider}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-mono text-xs text-slate-700">
          <div>
            {formatFecha(flight.fecha)}{" "}
            <span className="text-slate-400">({dowEs(flight.fecha)})</span>{" "}
            · {leg.approxHora ? `~${leg.approxHora}` : "~?"}
          </div>
          {leg.distanceNm != null || leg.flightTimeMin != null ? (
            <div className="text-[10px] text-slate-500">
              {leg.distanceNm != null
                ? `${Math.round(leg.distanceNm)} NM · `
                : ""}
              {leg.flightTimeMin ?? "?"} min
            </div>
          ) : null}
        </div>
        <StatusBadge status="ferry" />
      </div>
      <div className="mt-1 font-medium text-slate-900">
        {flight.aircraftDisplayName}
      </div>
      <div className="mt-1 truncate text-sm text-slate-800">
        {fromLabel} <span className="text-slate-500">→ {toLabel}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
        <div className="italic">
          <span className="text-slate-500">Tipo:</span> {tipo}
        </div>
        <div>
          <span className="text-slate-500">Cta:</span>{" "}
          <span className="font-mono">{flight.accountCode ?? "—"}</span>
        </div>
        <div>
          <span className="text-slate-500">Piloto:</span>{" "}
          {flight.pilotDisplayName ?? flight.pilotDisplayNameRequested ?? "—"}
        </div>
      </div>
    </div>
  );
}
